<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import GoalContentPageStandards from '../GoalContentPageStandards.vue'
import PowerStandard from '../PowerStandard.vue'
import StandardStaircase from '../StandardStaircase.vue'
import StandardsAccordion from '../StandardsAccordion.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import { ANCHOR_SIDEBAR_HEADING_CLASSES, anchorSidebarHeadingClasses } from './anchor_sidebar_headings'

const props = withDefaults(defineProps<{
  /** What a copied key standard is reported against, when not the anchor page itself. */
  standardCopySource?: { title: string, uuid: string }
}>(), {
  standardCopySource: undefined
})

const anchorPageStore = useAnchorPageStore()
const {
  content,
  hasAccordionObjectives,
  isFetchingStandards,
  isFetchingStaircase,
  keyStandard,
  groupedSidebarStandards,
  staircaseContent
} = storeToRefs(anchorPageStore)
const { editorVisibility } = storeToRefs(useVisibilityStore())

const title = computed(() => content.value?.title ?? '')
const contentUuid = computed(() => content.value?.uuid ?? '')
const standardCopyTitle = computed(() => props.standardCopySource?.title ?? title.value)
const standardCopyUuid = computed(() => props.standardCopySource?.uuid ?? contentUuid.value)
const isLoadingStandards = computed(() => isFetchingStandards.value || isFetchingStaircase.value)
const headingClasses = computed(() => [ANCHOR_SIDEBAR_HEADING_CLASSES, 'mt-9', hasAccordionObjectives.value ? 'mb-4' : 'mb-6'])
const sectionHeadingClasses = computed(() => anchorSidebarHeadingClasses(hasAccordionObjectives.value))
</script>

<template>
  <p v-if="isLoadingStandards" role="status" class="flex items-center text-gray-700" data-testid="standards-loading">
    <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
    Loading...
  </p>

  <template v-if="keyStandard">
    <h2 :class="headingClasses" data-testid="key-standard-heading">{{ keyStandard.standard_set }}</h2>
    <CopyTrackWrapper type="standard" :title="standardCopyTitle" :uuid="standardCopyUuid" gate-copy :demo="!standardCopySource && content?.demo">
      <StandardsAccordion v-if="hasAccordionObjectives" :standards="[keyStandard]" is-first-standard-open />
      <PowerStandard v-else :standard="keyStandard" />
    </CopyTrackWrapper>
  </template>

  <GoalContentPageStandards
    :grouped-standards="groupedSidebarStandards"
    :is-edit-link-shown="editorVisibility"
    :is-first-standard-open="!hasAccordionObjectives"
    :is-accordion-layout="hasAccordionObjectives"
  />

  <template v-if="staircaseContent.length > 0">
    <h2 :class="sectionHeadingClasses">Standard Staircase</h2>
    <CopyTrackWrapper type="staircase" :title="title" :uuid="contentUuid" gate-copy :demo="content?.demo">
      <StandardStaircase
        :page-uuid="contentUuid"
        :grades="content?.grades"
        :goal-staircase="staircaseContent"
        :is-anchor-page-label-shown="!hasAccordionObjectives"
      />
    </CopyTrackWrapper>
  </template>
</template>
